// ---------------------------------------------------------------------------
// LevelUpCRM - HTTP client
// ---------------------------------------------------------------------------
// รูปแบบ response ของ PaymentBroker-Backend
//   { status: true|false, data: ..., message: "...", pagination: {...} }
// token เก็บแบบเดียวกับ Zenithlink-Market: localStorage["scp-acc-tk"] = btoa("Bearer " + token)
// ---------------------------------------------------------------------------
import { API_BASE_URL, TOKEN_KEY, PROFILE_KEY, MOCK_LATENCY } from "./config";

const EXPIRED_MESSAGES = ["Token not found", "Token has expired"];

export class ApiError extends Error {
  constructor(message, payload) {
    super(message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    this.name = "ApiError";
    this.payload = payload;
  }
}

export const getRawToken = () => localStorage.getItem(TOKEN_KEY) || "";

// คืนค่า header Authorization ที่ถอดรหัสแล้ว ("Bearer xxx")
export const getAuthHeader = () => {
  const raw = getRawToken();
  if (!raw) return "";
  try {
    return atob(raw);
  } catch (e) {
    return "";
  }
};

export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, btoa("Bearer " + token));
  else localStorage.removeItem(TOKEN_KEY);
};

export const getStoredProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const setStoredProfile = (profile) => {
  if (profile) localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  else localStorage.removeItem(PROFILE_KEY);
};

export const clearSession = () => {
  setToken(null);
  setStoredProfile(null);
  window.dispatchEvent(new Event("crm-auth-change"));
};

const buildQuery = (params) => {
  if (!params) return "";
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  return "?" + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
};

const readMessage = (payload) => {
  if (!payload) return "";
  if (typeof payload.message === "string") return payload.message;
  if (payload.message?.message) return payload.message.message;
  if (payload.error) return String(payload.error);
  return "";
};

const request = async (method, path, { params, body, signal } = {}) => {
  const url = path.includes("://") ? path : `${API_BASE_URL}${path}`.replace(/\s/g, "") + buildQuery(params);
  const authorization = getAuthHeader();

  let payload = null;
  try {
    const response = await fetch(url, {
      method,
      signal,
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
        ...(authorization ? { Authorization: authorization } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    payload = await response.json();
  } catch (e) {
    return { status: false, data: null, message: "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ" };
  }

  const message = readMessage(payload);

  // token หมดอายุ -> เคลียร์ session ให้ layout พาไปหน้า login
  if (EXPIRED_MESSAGES.includes(message)) {
    clearSession();
    return { status: false, data: null, message, expired: true };
  }

  if (payload?.status) return payload;

  return { status: false, data: payload?.data ?? null, message: message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง" };
};

export const http = {
  get: (path, params, options) => request("GET", path, { ...options, params }),
  post: (path, body, options) => request("POST", path, { ...options, body }),
  put: (path, body, options) => request("PUT", path, { ...options, body }),
  delete: (path, options) => request("DELETE", path, options),
};

// ใช้เมื่อ caller อยากได้ data ตรง ๆ และให้ throw เมื่อ API ตอบไม่สำเร็จ
export const unwrap = (result) => {
  if (result?.status) return result.data;
  throw new ApiError(result?.message, result);
};

// จำลอง response ของ API พร้อม latency (ใช้กับ repository ที่ยังเป็น mock)
export const mockResponse = (data) =>
  new Promise((resolve) => setTimeout(() => resolve(typeof data === "function" ? data() : data), MOCK_LATENCY));
